import { getApiUrl } from '../api/api.config';

// PWA Service for managing PWA functionality
class PWAService {
  private deferredPrompt: any = null;
  private isInstalled = false;
  private registration: ServiceWorkerRegistration | null = null;
  private pushSubscription: PushSubscription | null = null;

  constructor() {
    this.initializePWA();
  }

  private async initializePWA() {
    // Register service worker
    await this.registerServiceWorker();
    
    // Set up install prompt handling
    this.setupInstallPrompt();
    
    // Check if already installed
    this.checkInstallStatus();
    
    // Request notification permission
    await this.requestNotificationPermission();
  }

  private async registerServiceWorker() {
    // In development, service worker caching can easily cause "half-updated" bundles
    // (stale JS/CSS) and very confusing mobile-only issues. Disable SW in dev and
    // proactively unregister any existing registrations.
    if (import.meta.env.DEV && 'serviceWorker' in navigator) {
      try {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map(r => r.unregister()));
      } catch (error) {
        console.warn('PWA: Failed to unregister service workers in dev:', error);
      }
      return;
    }

    if ('serviceWorker' in navigator) {
      try {
        this.registration = await navigator.serviceWorker.register('/sw.js', {
          scope: '/'
        });
        
        // Listen for service worker updates
        this.registration.addEventListener('updatefound', () => {
          this.handleServiceWorkerUpdate();
        });
        
        // Listen for messages from service worker
        navigator.serviceWorker.addEventListener('message', (event) => {
          this.handleServiceWorkerMessage(event.data);
        });
        
        // Wait for service worker to be ready
        await navigator.serviceWorker.ready;
        
        // Set up push notifications
        await this.setupPushNotifications();
        
      } catch (error) {
        console.error('PWA: Service Worker registration failed:', error);
      }
    }
  }

  private setupInstallPrompt() {
    window.addEventListener('beforeinstallprompt', (e) => {
      e.preventDefault();
      this.deferredPrompt = e;
      this.showInstallButton();
    });

    window.addEventListener('appinstalled', () => {
      this.isInstalled = true;
      this.hideInstallButton();
      this.deferredPrompt = null;
    });
  }

  private checkInstallStatus() {
    // Check if running in standalone mode (installed)
    if (window.matchMedia('(display-mode: standalone)').matches) {
      this.isInstalled = true;
    }
    
    // Check for iOS standalone mode
    if ((window.navigator as any).standalone) {
      this.isInstalled = true;
    }
  }

  public async installApp() {
    if (!this.deferredPrompt) {
      return false;
    }

    try {
      this.deferredPrompt.prompt();
      const result = await this.deferredPrompt.userChoice;
      
      if (result.outcome === 'accepted') {
        this.isInstalled = true;
        this.hideInstallButton();
      }
      
      this.deferredPrompt = null;
      return result.outcome === 'accepted';
    } catch (error) {
      console.error('PWA: Install failed:', error);
      return false;
    }
  }

  private showInstallButton() {
    // Dispatch custom event to show install button in UI
    window.dispatchEvent(new CustomEvent('pwa-install-available'));
  }

  private hideInstallButton() {
    // Dispatch custom event to hide install button in UI
    window.dispatchEvent(new CustomEvent('pwa-install-completed'));
  }

  private handleServiceWorkerUpdate() {
    // Dispatch custom event to notify about update
    window.dispatchEvent(new CustomEvent('pwa-update-available'));
  }

  private handleServiceWorkerMessage(data: any) {
    switch (data.type) {
      case 'SW_UPDATED':
        // Show update notification to user
        window.dispatchEvent(new CustomEvent('pwa-update-ready', {
          detail: {
            version: data.version,
            message: data.message
          }
        }));
        break;
      case 'NAVIGATE_TO_TASK':
        // Handle navigation from push notification
        window.dispatchEvent(new CustomEvent('pwa-navigate-to-task', {
          detail: data.data
        }));
        break;
      default:
        // Unknown message type
        break;
    }
  }

  private async requestNotificationPermission(): Promise<boolean> {
    if (!('Notification' in window)) {
      return false;
    }

    // Check if iOS Safari (not PWA) - push notifications not supported
    if (this.isIOSSafari() && !this.isAppInstalled()) {
      console.warn('PWA: Push notifications require PWA installation on iOS Safari');
      return false;
    }

    if (Notification.permission === 'granted') {
      return true;
    }

    if (Notification.permission === 'denied') {
      return false;
    }

    try {
      const permission = await Notification.requestPermission();
      
      // Some browsers might not support the promise-based API
      if (permission === undefined) {
        return new Promise((resolve) => {
          const legacyPermission = Notification.requestPermission((result) => {
            resolve(result === 'granted');
          });
          
          // Handle case where it returns a promise anyway
          if (legacyPermission && typeof legacyPermission.then === 'function') {
            legacyPermission.then((result) => {
              resolve(result === 'granted');
            });
          }
        });
      }
      
      return permission === 'granted';
    } catch (error) {
      console.error('PWA: Failed to request notification permission:', error);
      return false;
    }
  }

  private async setupPushNotifications() {
    if (!this.registration) {
      return;
    }

    if (!('PushManager' in window)) {
      return;
    }

    try {
      // Check if already subscribed
      this.pushSubscription = await this.registration.pushManager.getSubscription();
      
      if (this.pushSubscription) {
        await this.syncPushSubscription();
      }
    } catch (error) {
      console.error('PWA: Failed to check push subscription:', error);
    }
  }

  public async subscribeToPushNotifications(): Promise<boolean> {
    if (!this.registration) {
      console.error('PWA: No service worker registration');
      return false;
    }

    // Check if push notifications are supported
    if (!('PushManager' in window)) {
      console.error('PWA: Push notifications not supported in this browser');
      return false;
    }

    // Check if notifications are supported
    if (!('Notification' in window)) {
      console.error('PWA: Notifications not supported in this browser');
      return false;
    }

    // iOS Safari (not PWA) doesn't support push notifications
    if (this.isIOSSafari() && !this.isAppInstalled()) {
      console.error('PWA: Push notifications require PWA installation on iOS Safari. Please add the app to your home screen first.');
      throw new Error('iOS_PWA_REQUIRED');
    }

    if (Notification.permission !== 'granted') {
      const granted = await this.requestNotificationPermission();
      if (!granted) {
        return false;
      }
    }

    try {
      // Get VAPID public key from server
      const vapidPublicKey = await this.getVapidPublicKey();
      
      if (!vapidPublicKey) {
        throw new Error('Failed to get VAPID public key from server');
      }
      
      // Check if already subscribed
      const existingSubscription = await this.registration.pushManager.getSubscription();
      if (existingSubscription) {
        await existingSubscription.unsubscribe();
      }

      this.pushSubscription = await this.registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: this.urlBase64ToUint8Array(vapidPublicKey)
      });
      
      // Send subscription to server
      await this.syncPushSubscription();
      
      return true;
    } catch (error) {
      console.error('PWA: Failed to subscribe to push notifications:', error);
      
      // Log specific error details and handle iOS-specific errors
      if (error instanceof Error) {
        console.error('PWA: Error name:', error.name);
        console.error('PWA: Error message:', error.message);
        console.error('PWA: Error stack:', error.stack);
        
        // Handle specific iOS errors
        if (error.message === 'iOS_PWA_REQUIRED') {
          console.error('PWA: iOS requires PWA installation for push notifications');
        } else if (error.name === 'NotAllowedError' && this.isIOSSafari()) {
          console.error('PWA: iOS Safari blocked push notification permission');
        } else if (error.name === 'NotSupportedError' && this.isIOSSafari()) {
          console.error('PWA: Push notifications not supported in iOS Safari browser mode');
        }
      }
      
      return false;
    }
  }

  public async unsubscribeFromPushNotifications(): Promise<boolean> {
    if (!this.pushSubscription) {
      return true;
    }

    try {
      await this.pushSubscription.unsubscribe();
      
      // Remove subscription from server
      await this.removePushSubscription();
      
      this.pushSubscription = null;
      return true;
    } catch (error) {
      console.error('PWA: Failed to unsubscribe from push notifications:', error);
      return false;
    }
  }

  private async syncPushSubscription() {
    if (!this.pushSubscription) return;

    try {
      const userEmail = this.getCurrentUserEmail();
      if (!userEmail) {
        return;
      }

      const deviceId = this.getDeviceId();
      const deviceName = this.getDeviceName();

      const response = await fetch(getApiUrl('pushnotifications/subscribe'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userEmail,
          deviceId,
          deviceName,
          subscription: this.pushSubscription.toJSON()
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to sync push subscription: ${response.status}`);
      }
    } catch (error) {
      console.error('PWA: Failed to sync push subscription:', error);
    }
  }

  private async removePushSubscription() {
    try {
      const userEmail = this.getCurrentUserEmail();
      if (!userEmail) return;

      const deviceId = this.getDeviceId();

      const response = await fetch(getApiUrl('pushnotifications/unsubscribe'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          userEmail,
          deviceId 
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to remove push subscription: ${response.status}`);
      }
    } catch (error) {
      console.error('PWA: Failed to remove push subscription:', error);
    }
  }

  private async getVapidPublicKey(): Promise<string | null> {
    try {
      const response = await fetch(getApiUrl('pushnotifications/vapid-public-key'));
      if (!response.ok) {
        throw new Error(`Failed to get VAPID key: ${response.status}`);
      }
      const data = await response.json();
      return data.publicKey;
    } catch (error) {
      console.error('PWA: Failed to get VAPID public key:', error);
      return null;
    }
  }

  private getCurrentUserEmail(): string | null {
    // Try to get user email from localStorage (where UserContext stores it)
    try {
      // Check both storage keys used by UserContext
      const storedUser = localStorage.getItem('user') || localStorage.getItem('currentUser');
      if (storedUser) {
        const userData = JSON.parse(storedUser);
        return userData.email || null;
      }
    } catch (error) {
      console.error('PWA: Failed to get user email from localStorage:', error);
    }
    return null;
  }

  private getDeviceId(): string {
    // Get or generate a unique device ID
    let deviceId = localStorage.getItem('symphonytodo-device-id');
    if (!deviceId) {
      // Generate a unique device ID
      deviceId = 'device_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
      localStorage.setItem('symphonytodo-device-id', deviceId);
    }
    return deviceId;
  }

  private getDeviceName(): string {
    // Generate a user-friendly device name
    const userAgent = navigator.userAgent;
    let deviceName = 'Unknown Device';
    
    // Detect platform
    if (/Android/.test(userAgent)) {
      deviceName = 'Android Device';
    } else if (/iPhone|iPad|iPod/.test(userAgent)) {
      deviceName = /iPad/.test(userAgent) ? 'iPad' : 'iPhone';
    } else if (/Windows/.test(userAgent)) {
      deviceName = 'Windows PC';
    } else if (/Macintosh|Mac OS X/.test(userAgent)) {
      deviceName = 'Mac';
    } else if (/Linux/.test(userAgent)) {
      deviceName = 'Linux Device';
    }
    
    // Add browser info
    if (/Chrome/.test(userAgent) && !/Edge/.test(userAgent)) {
      deviceName += ' (Chrome)';
    } else if (/Firefox/.test(userAgent)) {
      deviceName += ' (Firefox)';
    } else if (/Safari/.test(userAgent) && !/Chrome/.test(userAgent)) {
      deviceName += ' (Safari)';
    } else if (/Edge/.test(userAgent)) {
      deviceName += ' (Edge)';
    }
    
    return deviceName;
  }

  private urlBase64ToUint8Array(base64String: string): Uint8Array {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }

  public isAppInstalled(): boolean {
    return this.isInstalled;
  }

  public isInstallPromptAvailable(): boolean {
    return this.deferredPrompt !== null;
  }

  public isPushNotificationsSupported(): boolean {
    return 'serviceWorker' in navigator && 'PushManager' in window;
  }

  public isPushNotificationsEnabled(): boolean {
    return this.pushSubscription !== null;
  }

  public getNotificationPermission(): NotificationPermission {
    return 'Notification' in window ? Notification.permission : 'default';
  }

  // Handle app shortcuts
  public handleShortcut(shortcut: string) {
    switch (shortcut) {
      case 'new-task':
        window.dispatchEvent(new CustomEvent('pwa-shortcut-new-task'));
        break;
      case 'lists':
        window.dispatchEvent(new CustomEvent('pwa-shortcut-lists'));
        break;
      default:
        // Unknown shortcut
        break;
    }
  }

  // Check URL for shortcuts
  public checkForShortcuts() {
    const urlParams = new URLSearchParams(window.location.search);
    const shortcut = urlParams.get('shortcut');
    if (shortcut) {
      this.handleShortcut(shortcut);
    }
  }

  // Force update the PWA by triggering service worker update
  public async forceUpdate(): Promise<boolean> {
    if (!this.registration) {
      return false;
    }

    try {
      await this.registration.update();
      return true;
    } catch (error) {
      console.error('PWA: Failed to check for service worker updates:', error);
      return false;
    }
  }

  // Reload the app to apply updates
  public reloadApp() {
    window.location.reload();
  }

  // iOS detection utilities
  private isIOSSafari(): boolean {
    const userAgent = window.navigator.userAgent;
    const isIOS = /iPad|iPhone|iPod/.test(userAgent);
    const isSafari = /Safari/.test(userAgent) && !/Chrome|CriOS|FxiOS|EdgiOS/.test(userAgent);
    return isIOS && isSafari;
  }

  public getIOSPushNotificationStatus(): 'not-supported' | 'requires-pwa' | 'supported' {
    if (!this.isIOSSafari()) {
      return 'supported';
    }
    
    if (!this.isAppInstalled()) {
      return 'requires-pwa';
    }
    
    return 'supported';
  }

  public getPushNotificationStatusMessage(): string {
    const status = this.getIOSPushNotificationStatus();
    
    switch (status) {
      case 'requires-pwa':
        return 'To receive push notifications on iOS, please add this app to your home screen first. Tap the Share button and select "Add to Home Screen".';
      case 'not-supported':
        return 'Push notifications are not supported in this browser.';
      case 'supported':
        return '';
      default:
        return '';
    }
  }

  public async getUserDevices(): Promise<any[]> {
    try {
      const userEmail = this.getCurrentUserEmail();
      if (!userEmail) {
        return [];
      }

      const response = await fetch(getApiUrl(`pushnotifications/devices/${encodeURIComponent(userEmail)}`));
      if (!response.ok) {
        throw new Error(`Failed to get user devices: ${response.status}`);
      }

      const data = await response.json();
      return data.devices || [];
    } catch (error) {
      console.error('PWA: Failed to get user devices:', error);
      return [];
    }
  }

  public getCurrentDeviceId(): string {
    return this.getDeviceId();
  }

  public getCurrentDeviceName(): string {
    return this.getDeviceName();
  }
}

// Export singleton instance
export const pwaService = new PWAService();

// Types for TypeScript
export interface PWAInstallEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

declare global {
  interface WindowEventMap {
    'beforeinstallprompt': PWAInstallEvent;
    'appinstalled': Event;
    'pwa-install-available': CustomEvent;
    'pwa-install-completed': CustomEvent;
    'pwa-update-available': CustomEvent;
    'pwa-update-ready': CustomEvent;
    'pwa-navigate-to-task': CustomEvent;
    'pwa-shortcut-new-task': CustomEvent;
    'pwa-shortcut-lists': CustomEvent;
  }
}
